import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wrench, CheckCircle, XCircle, Clock, AlertTriangle, Play, RefreshCw } from 'lucide-react';
import { beesClient } from '../../api/beesClient.js';

interface AutoFixData {
  totalIssues?: number;
  fixedIssues?: number;
  pendingIssues?: number;
  failedFixes?: number;
  availableFixes?: Array<{
    id: string;
    title: string;
    description: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    status: 'available' | 'running' | 'completed' | 'failed';
    estimatedTime?: string;
  }>;
  recentFixes?: Array<{
    id: string;
    title: string;
    status: 'success' | 'failed' | 'running';
    timestamp: string;
    duration?: string;
    details?: string;
  }>;
}

const AutoFixPanel: React.FC = () => {
  const [data, setData] = useState<AutoFixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningFixes, setRunningFixes] = useState<Set<string>>(new Set());

  const fetchAutoFixData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await beesClient.runAutoFix({});
      
      if (response.success && response.data) {
        setData(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch AutoFix data');
      }
    } catch (err) {
      console.error('AutoFix API error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      
      setData({
        totalIssues: 12,
        fixedIssues: 8,
        pendingIssues: 3,
        failedFixes: 1,
        availableFixes: [
          {
            id: 'fix-1',
            title: 'Update deprecated dependencies',
            description: 'Replace outdated npm packages with their latest versions',
            severity: 'medium',
            status: 'available',
            estimatedTime: '5 minutes'
          },
          {
            id: 'fix-2',
            title: 'Fix TypeScript type errors',
            description: 'Resolve 3 TypeScript compilation errors in components',
            severity: 'high',
            status: 'available',
            estimatedTime: '2 minutes'
          },
          {
            id: 'fix-3',
            title: 'Optimize bundle size',
            description: 'Remove unused imports and optimize webpack configuration',
            severity: 'low',
            status: 'completed',
            estimatedTime: '10 minutes'
          }
        ],
        recentFixes: [
          {
            id: 'recent-1',
            title: 'Fixed ESLint warnings',
            status: 'success',
            timestamp: '2 minutes ago',
            duration: '1m 23s',
            details: 'Resolved 15 linting issues across 8 files'
          },
          {
            id: 'recent-2',
            title: 'Updated security vulnerabilities',
            status: 'success',
            timestamp: '1 hour ago',
            duration: '3m 45s',
            details: 'Patched 2 high-severity npm audit issues'
          },
          {
            id: 'recent-3',
            title: 'Database migration fix',
            status: 'failed',
            timestamp: '3 hours ago',
            duration: '45s',
            details: 'Migration failed due to constraint violation'
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutoFixData();
  }, []);

  const handleRunFix = async (fixId: string) => {
    setRunningFixes(prev => new Set(prev).add(fixId));
    
    try {
      const response = await beesClient.runAutoFix({ fixId, action: 'run' });
      
      if (response.success) {
        await fetchAutoFixData();
      } else {
        console.error('Failed to run fix:', response.error);
      }
    } catch (err) {
      console.error('Error running fix:', err);
    } finally {
      setRunningFixes(prev => {
        const newSet = new Set(prev);
        newSet.delete(fixId);
        return newSet;
      });
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-900/20 border-red-500/30';
      case 'high': return 'text-orange-400 bg-orange-900/20 border-orange-500/30';
      case 'medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
      case 'low': return 'text-blue-400 bg-blue-900/20 border-blue-500/30';
      default: return 'text-gray-400 bg-gray-900/20 border-gray-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'running': return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'available': return <Clock className="w-4 h-4 text-yellow-400" />;
      default: return <AlertTriangle className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-3">
              <RefreshCw className="w-6 h-6 text-brand-accent animate-spin" />
              <span className="text-brand-text text-lg">Loading AutoFix data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="flex items-center justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg">
              <Wrench className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold violett-gradient-text text-drop-shadow mb-2">AutoFix Panel</h1>
          <p className="text-brand-text-secondary text-lg">Automated issue detection and resolution</p>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-900/20 border border-red-500/30 rounded-lg p-4"
          >
            <div className="flex items-center space-x-3">
              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-red-400 font-medium">AutoFix API Error</p>
                <p className="text-red-300 text-sm mt-1">{error}</p>
                <p className="text-red-300 text-xs mt-2">Showing mock data for demonstration</p>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'Total Issues', value: data?.totalIssues || 0, icon: AlertTriangle, color: 'text-yellow-400' },
            { label: 'Fixed Issues', value: data?.fixedIssues || 0, icon: CheckCircle, color: 'text-green-400' },
            { label: 'Pending Issues', value: data?.pendingIssues || 0, icon: Clock, color: 'text-blue-400' },
            { label: 'Failed Fixes', value: data?.failedFixes || 0, icon: XCircle, color: 'text-red-400' }
          ].map(({ label, value, icon: Icon, color }) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-dark rounded-xl p-6 border border-white/10"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-text-secondary text-sm font-medium">{label}</p>
                  <p className="text-2xl font-bold text-brand-text mt-1">{value}</p>
                </div>
                <Icon className={`w-8 h-8 ${color}`} />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass-dark rounded-xl p-6 border border-white/10"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold cream-gradient-text text-drop-shadow">Available Fixes</h2>
              <button
                onClick={fetchAutoFixData}
                className="p-2 bg-brand-accent/20 hover:bg-brand-accent/30 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4 text-brand-accent" />
              </button>
            </div>
            
            <div className="space-y-4">
              {data?.availableFixes?.map((fix) => (
                <div key={fix.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-medium cream-gradient-text text-drop-shadow">{fix.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(fix.severity)}`}>
                          {fix.severity}
                        </span>
                      </div>
                      <p className="text-brand-text-secondary text-sm">{fix.description}</p>
                      {fix.estimatedTime && (
                        <p className="text-brand-text-secondary text-xs mt-1">
                          Est. time: {fix.estimatedTime}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {getStatusIcon(fix.status)}
                      {fix.status === 'available' && (
                        <button
                          onClick={() => handleRunFix(fix.id)}
                          disabled={runningFixes.has(fix.id)}
                          className="p-2 bg-green-600/20 hover:bg-green-600/30 disabled:bg-gray-600/20 rounded-lg transition-colors"
                        >
                          {runningFixes.has(fix.id) ? (
                            <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 text-green-400" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="glass-dark rounded-xl p-6 border border-white/10"
          >
            <h2 className="text-xl font-semibold cream-gradient-text text-drop-shadow mb-6">Recent Fixes</h2>
            
            <div className="space-y-4">
              {data?.recentFixes?.map((fix) => (
                <div key={fix.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        {getStatusIcon(fix.status)}
                        <h3 className="font-medium cream-gradient-text text-drop-shadow">{fix.title}</h3>
                      </div>
                      <p className="text-brand-text-secondary text-sm">{fix.details}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-brand-text-secondary mt-3">
                    <span>{fix.timestamp}</span>
                    {fix.duration && <span>Duration: {fix.duration}</span>}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AutoFixPanel;
